<template>
  <Drawer
    :on-close="closePanel"
    :close-icon-position="closeIconPosition"
    :close-title="I18n.t('editor.modal_close')"
    :class="adjustDrawerStyles"
    container-padding-class="!py-4 sm:p-6 scrollable-drawer"
  >
    <template #content>
      <VerticalScrollContentContainer>
        <div class="drawer-mobile-padding flex flex-col gap-3 sm:gap-2 w-full">
          <CanvaCtaNew
            :image="store.selectedImage"
            :persistentStore="props.persistentStore"
            :refreshAllHdLayers="props.refreshAllHdLayers"
            :stage="props.stage"
            :stageHd="props.stageHd"
            :getDataURL="props.getDataURL"
            :canvaVariant="CanvaVariant.INLINE_EDITOR_DEFAULT"
            :canvaCtaIdentifier="CanvaCTAIdentifier.COLOR_AND_LIGHT"
            :icon="IconSliders"
            :label="I18n.t('canva_cta.flows.color_and_light')"
          />
          <CanvaCtaNew
            :image="store.selectedImage"
            :persistentStore="props.persistentStore"
            :refreshAllHdLayers="props.refreshAllHdLayers"
            :stage="props.stage"
            :stageHd="props.stageHd"
            :getDataURL="props.getDataURL"
            :canvaVariant="CanvaVariant.INLINE_EDITOR_DEFAULT"
            :canvaCtaIdentifier="CanvaCTAIdentifier.CROP_AND_PLACE"
            :icon="IconCrop"
            :label="I18n.t('canva_cta.flows.crop_and_place')"
          />
          <CanvaCtaNew
            :image="store.selectedImage"
            :persistentStore="props.persistentStore"
            :refreshAllHdLayers="props.refreshAllHdLayers"
            :stage="props.stage"
            :stageHd="props.stageHd"
            :getDataURL="props.getDataURL"
            :canvaVariant="CanvaVariant.INLINE_EDITOR_DEFAULT"
            :canvaCtaIdentifier="CanvaCTAIdentifier.EXPAND_IMAGE"
            :icon="IconExpand"
            :label="I18n.t('canva_cta.flows.expand_image')"
          />
          <div class="flex flex-col gap-2 sm:pt-3 hide-on-tablet">
            <CanvaContentImage
              :image="store.selectedImage"
              :src="adjustContentImage"
              alt="Crop and place your image in Canva"
              :getDataURL="props.getDataURL"
              :canvaVariant="CanvaVariant.INLINE_EDITOR_DEFAULT"
              :canvaCtaIdentifier="CanvaCTAIdentifier.CROP_AND_PLACE_VISUAL"
              :stage="props.stage"
              :stageHd="props.stageHd"
              :persistentStore="props.persistentStore"
              :refreshAllHdLayers="props.refreshAllHdLayers"
            />
          </div>
        </div>
      </VerticalScrollContentContainer>
      <div class="mobile-asterisk">
        <hr class="divider flex" />
        <p class="text-typo-secondary text-xs text-center mt-2 mb-1 !px-4" v-html="tos_html" />
      </div>
    </template>
  </Drawer>
</template>

<script setup lang="ts">
import "@/src/i18n";
import { computed, inject } from "vue";
import { Drawer } from "prism";

import CanvaCtaNew from "@/components/canva_cta_new.vue";
import { useEditorStore } from "@/stores/editor_store";
import { Stage } from "konva/lib/Stage";
import { CanvaCTAIdentifier, CanvaVariant } from "@/composables/canva_link_builder";
import adjustContentImage from "@assets/images/inline-editor/adjust_panel_content.png";
import IconExpand from "@/components/upload/editor_components/side_panel_icons/icon-maximize.vue";
import IconCrop from "@/components/upload/editor_components/side_panel_icons/icon-crop.vue";
import IconSliders from "@/components/upload/editor_components/side_panel_icons/icon-sliders.vue";
import CanvaContentImage from "@/components/upload/canva_content_image.vue";
import { I18n as I18nType } from "i18n-js";
import { PersistentStore } from "@/stores/persistent_store";
import { Image } from "@/modules/internal_api/image";
import { usePhotoEditor } from "@/composables/photo_editor";
import VerticalScrollContentContainer from "@/components/prism/vertical_scroll_content_container.vue";

const I18n: I18nType = inject("I18n");
const store = useEditorStore();

interface AdjustProps {
  editor: HTMLElement;
  stage: Stage;
  stageHd: Promise<Stage>;
  getDataURL: Function;
  persistentStore: PersistentStore;
  refreshAllHdLayers: (image: Image) => Promise<void>;
}

const { isNewEditorEnabled: isNewPhotoEditor } = usePhotoEditor();

const closeIconPosition = computed(() => {
  return isNewPhotoEditor.value ? "none" : "floating";
});

const props = defineProps<AdjustProps>();

const tos_html = I18n.t("canva_cta.common.tos_link_html", { link: "https://www.canva.com/policies/terms-of-use/" });

const emit = defineEmits<{
  (e: "closePanel"): void;
}>();

const closePanel = () => {
  emit("closePanel");
};

const adjustDrawerStyles = computed(() => {
  return isNewPhotoEditor.value ? "" : "adjustDrawer";
});
</script>

<style scoped lang="scss">
.adjustDrawer {
  @media (min-width: 1024px) {
    width: 24.25rem;
  }
}

.drawer-mobile-padding {
  @media (max-width: 1023px) {
    padding: 0 0.75rem;
  }
}

.mobile-asterisk {
  @media (min-width: 1024px) {
    display: none;
  }
}
</style>
